import React from 'react';

import { BrandPendulumMark } from './BrandPendulumMark';
import { LoadingSpinner } from './LoadingSpinner';
import { PlanningLoadingBar } from './PlanningWorkspaceGate/PlanningLoadingBar';

import './PlanningRouteShell.css';

const PLANNING_PATH_PATTERN = /^\/planning(?:\/|$)/;

const PLANNING_NAV_ITEMS = [
  'Tasks',
  'System requirements',
  'User Flow',
  'Design',
  'Code',
  'Architecture',
  'Config',
  'Preview',
  'Cluster K8s',
  'Team',
  'Organization',
  'Deploy to production',
] as const;

export const isPlanningPath = (pathname: string): boolean => PLANNING_PATH_PATTERN.test(pathname);

export const hasInitialProjectBootstrap = (state: unknown): boolean => (
  (state as { landingBootstrap?: { initialProjectBootstrap?: unknown } } | null)
    ?.landingBootstrap?.initialProjectBootstrap === true
);

/** A first-message handoff already has useful content, so it does not need the restoration skeleton. */
export const shouldSkipPlanningSkeleton = (
  navigationType: string,
  initialProjectBootstrap: boolean,
): boolean => navigationType === 'PUSH' && initialProjectBootstrap;

const PlanningShellSectionToggle: React.FC = () => (
  <div className="planning-route-shell__section-toggle" aria-hidden="true">
    <span className="planning-route-shell__section-toggle-tab planning-route-shell__section-toggle-tab--active">
      <span className="planning-route-shell__section-toggle-icon" />
      <span className="planning-route-shell__section-toggle-label" />
    </span>
    <span className="planning-route-shell__section-toggle-tab">
      <span className="planning-route-shell__section-toggle-icon" />
      <span className="planning-route-shell__section-toggle-label" />
    </span>
  </div>
);

const PlanningShellNav: React.FC = () => (
  <nav className="planning-route-shell__nav">
    {PLANNING_NAV_ITEMS.map((label, index) => (
      <div
        className={`planning-route-shell__nav-item${index === 0 ? ' planning-route-shell__nav-item--active' : ''}`}
        key={label}
      >
        <span className="planning-route-shell__nav-icon" />
        <span className="planning-route-shell__nav-label">{label}</span>
        <span className="planning-route-shell__status" />
      </div>
    ))}
  </nav>
);

const PlanningShellSidebarFooter: React.FC = () => (
  <>
    <div className="planning-route-shell__support">
      <span className="planning-route-shell__support-icon" />
      <span className="planning-route-shell__line planning-route-shell__line--support" />
    </div>
    <div className="planning-route-shell__footer">
      <span className="planning-route-shell__avatar planning-route-shell__avatar--large" />
      <span className="planning-route-shell__footer-button" />
      <span className="planning-route-shell__footer-control" />
      <span className="planning-route-shell__footer-control" />
      <span className="planning-route-shell__footer-control" />
    </div>
  </>
);

const PlanningShellSidebar: React.FC = () => (
  <aside className="planning-route-shell__sidebar" aria-hidden="true">
    <div className="planning-route-shell__brand">
      <BrandPendulumMark className="planning-route-shell__brand-mark" />
      <span>8080.AI</span>
    </div>
    <div className="planning-route-shell__project">
      <span className="planning-route-shell__project-icon" />
      <span className="planning-route-shell__line planning-route-shell__line--project" />
      <span className="planning-route-shell__chevron" />
    </div>
    <PlanningShellSectionToggle />
    <PlanningShellNav />
    <PlanningShellSidebarFooter />
  </aside>
);

type PlanningShellProgressProps = {
  /** Real-progress lower bound from the workspace gate (0–100). */
  progressFloor?: number;
  /** Fill the bar to 100% — the workspace is about to appear. */
  progressComplete?: boolean;
};

const PlanningShellStage: React.FC<PlanningShellProgressProps> = ({ progressFloor, progressComplete }) => (
  <div className="planning-route-shell__stage-loader">
    <span className="planning-route-shell__stage-loader-label">Loading workspace…</span>
    <PlanningLoadingBar floor={progressFloor} complete={progressComplete} />
  </div>
);

const PlanningShellComposer: React.FC = () => (
  <div className="planning-route-shell__composer" aria-hidden="true">
    <div className="planning-route-shell__composer-message">
      <span className="planning-route-shell__line planning-route-shell__line--composer" />
    </div>
    <div className="planning-route-shell__composer-actions">
      <div className="planning-route-shell__composer-group planning-route-shell__composer-group--left">
        <span className="planning-route-shell__composer-control planning-route-shell__composer-attach" />
        <span className="planning-route-shell__composer-control planning-route-shell__composer-visibility" />
      </div>
      <div className="planning-route-shell__composer-group planning-route-shell__composer-group--right">
        <span className="planning-route-shell__composer-control planning-route-shell__composer-agents" />
        <span className="planning-route-shell__composer-control planning-route-shell__composer-keys" />
        <span className="planning-route-shell__composer-control planning-route-shell__composer-auto" />
        <span className="planning-route-shell__composer-control planning-route-shell__composer-settings" />
        <span className="planning-route-shell__composer-send" />
      </div>
    </div>
  </div>
);

export type PlanningRouteShellProps = PlanningShellProgressProps & {
  /** Float above the mounted workspace instead of replacing it (workspace gate). */
  overlay?: boolean;
  /** Overlay is fading out; pointer events already pass through. */
  releasing?: boolean;
  /** Paint the composer skeleton (false for workstation views that hide chat). */
  composer?: boolean;
};

const shellClassName = ({ overlay, releasing }: PlanningRouteShellProps): string => [
  'planning-route-shell',
  overlay ? 'planning-route-shell--overlay' : '',
  releasing ? 'planning-route-shell--releasing' : '',
].filter(Boolean).join(' ');

/**
 * Geometry-stable shell shared by the pre-auth, lazy-route and workspace-gate
 * loading states.
 */
export const PlanningRouteShell: React.FC<PlanningRouteShellProps> = (props) => (
  <div
    className={shellClassName(props)}
    role="status"
    aria-label="Loading planning workspace"
    aria-live="polite"
    aria-hidden={props.releasing || undefined}
  >
    <PlanningShellSidebar />
    <main className="planning-route-shell__main">
      <PlanningShellStage progressFloor={props.progressFloor} progressComplete={props.progressComplete} />
      {props.composer !== false && <PlanningShellComposer />}
    </main>
  </div>
);

type RouteLoadingFallbackProps = {
  pathname?: string;
  skipPlanningSkeleton?: boolean;
};

/** Route-aware fallback used by the app-level Suspense boundary. */
export const RouteLoadingFallback: React.FC<RouteLoadingFallbackProps> = ({
  pathname = window.location.pathname,
  skipPlanningSkeleton = false,
}) => {
  if (isPlanningPath(pathname) && skipPlanningSkeleton) {
    return (
      <div
        className="protected-wrapper"
        role="status"
        aria-label="Opening planning workspace"
        aria-live="polite"
      />
    );
  }

  if (isPlanningPath(pathname) && !skipPlanningSkeleton) {
    return <PlanningRouteShell />;
  }

  return (
    <div
      className="protected-wrapper"
      role="status"
      aria-label="Loading page"
      aria-live="polite"
    >
      <LoadingSpinner size={40} />
    </div>
  );
};
